import { useMemo, useState } from "react";
import { api, type APIResult } from "../../api/client";
import { formatDuration, formatMeters } from "../../lib/format";
import { JSONEditor } from "../JSONEditor";
import { PageHeader } from "../PageHeader";
import { ResponseCard } from "../ResponseCard";
import { SectionCard } from "../SectionCard";
import type { MatrixResponse, Profile } from "../../types/routecore";

// Coords default cobrindo pontos em Chapecó - SC (alinhado com os
// exemplos JSON).
const DEFAULT_ORIGINS = [
  { lat: -27.0996, lon: -52.6188 }, // Centro (depósito)
  { lat: -27.10065, lon: -52.60144 }, // UFFS / Universitário
];
const DEFAULT_DESTINATIONS = [
  { lat: -27.10478, lon: -52.56028 }, // Bela Vista
  { lat: -27.08882, lon: -52.58583 }, // Efapi
  { lat: -27.08301, lon: -52.59164 }, // Maria Goretti
];

export function TabMatrix() {
  const [origins, setOrigins] = useState<string>(JSON.stringify(DEFAULT_ORIGINS, null, 2));
  const [destinations, setDestinations] = useState<string>(
    JSON.stringify(DEFAULT_DESTINATIONS, null, 2),
  );
  const [profile, setProfile] = useState<Profile>("car");
  const [result, setResult] = useState<APIResult<MatrixResponse> | null>(null);
  const [loading, setLoading] = useState(false);

  async function run() {
    try {
      const payload = {
        origins: JSON.parse(origins),
        destinations: JSON.parse(destinations),
        profile,
      };
      setLoading(true);
      setResult(null);
      const r = await api.matrix(payload);
      setResult(r as APIResult<MatrixResponse>);
    } catch (err) {
      setResult({
        ok: false,
        status: 0,
        data: null,
        raw: String(err),
        latencyMs: 0,
      });
    } finally {
      setLoading(false);
    }
  }

  const distances = result?.data?.distances_meters;
  const durations = result?.data?.durations_seconds;
  const meta = result?.data?.metadata;

  const headerCols = useMemo(() => {
    try {
      return (JSON.parse(destinations) as unknown[]).map((_, i) => `D${i + 1}`);
    } catch {
      return [] as string[];
    }
  }, [destinations]);

  const rowLabels = useMemo(() => {
    try {
      return (JSON.parse(origins) as unknown[]).map((_, i) => `O${i + 1}`);
    } catch {
      return [] as string[];
    }
  }, [origins]);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Matriz origem-destino"
        subtitle="Repassa para o OSRM. Resultado em metros (distância) e segundos (duração)."
        endpoint={{ method: "POST", path: "/v1/matrix" }}
        actions={
          <button type="button" className="ks-btn-primary" disabled={loading} onClick={run}>
            {loading ? "calculando..." : "Calcular matriz"}
          </button>
        }
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <SectionCard title="Origens (lat/lon)" description="Array de objetos {lat, lon}.">
          <JSONEditor value={origins} onChange={setOrigins} rows={10} />
        </SectionCard>
        <SectionCard title="Destinos (lat/lon)" description="Array de objetos {lat, lon}.">
          <JSONEditor value={destinations} onChange={setDestinations} rows={10} />
        </SectionCard>
        <SectionCard title="Profile">
          <div className="space-y-2">
            {(["car", "motorcycle", "van", "truck"] as Profile[]).map((p) => (
              <label key={p} className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  checked={profile === p}
                  onChange={() => setProfile(p)}
                  className="size-4 accent-brand-600"
                />
                <code className="ks-code">{p}</code>
              </label>
            ))}
            <p className="mt-2 text-[11px] text-ink-500">
              No MVP, o RouteCore tem apenas o perfil <code>car</code> compilado por padrão.
            </p>
          </div>
        </SectionCard>
      </div>

      {distances && durations && (
        <SectionCard
          title="Tabela formatada"
          description={
            meta
              ? `fonte: ${meta.source} · cached: ${meta.cached ? "sim" : "não"}`
              : undefined
          }
        >
          <div className="ks-scroll overflow-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="border-b border-ink-200 text-left text-ink-700">
                  <th className="px-2 py-1"></th>
                  {headerCols.map((c) => (
                    <th key={c} className="px-2 py-1 font-mono">
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {distances.map((row, i) => (
                  <tr key={i} className="border-b border-ink-100">
                    <th className="bg-ink-50 px-2 py-1 text-left font-mono text-ink-700">
                      {rowLabels[i] ?? `O${i + 1}`}
                    </th>
                    {row.map((d, j) => (
                      <td key={j} className="px-2 py-1">
                        <div className="font-mono text-ink-900">{formatMeters(d)}</div>
                        <div className="font-mono text-[10px] text-ink-500">
                          {formatDuration(durations[i]?.[j])}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
      )}

      <ResponseCard result={result} loading={loading} />
    </div>
  );
}
